import classNames from 'classnames';
import { Link } from 'react-router';

import { WIZE_SUBSCRIPTION_NAME } from 'my-core/constants';
import { useDialogStateManager } from 'my-core/hooks';
import { hasProgress } from 'my-core/online-course-user-utils';
import { onlineCourseResumePath } from 'my-core/routes';
import {
  FREE_TOKEN_COUNT,
  getHasPaymentIssue,
  getSubscriptionStatus,
  PREMED_PLUS_PRICE,
  SUBSCRIPTION_PRICE,
} from 'my-core/subscription-utils';
import { numberToCurrency, pluralize } from 'my-utils';

import OnlineCourseProgressBreakdown from 'my-components/OnlineCourseProgressBreakdown';
import SubscriptionCheckout from 'my-components/SubscriptionCheckout';
import CustomColorButton from 'my-elements/CustomColorButton';

import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import { faCoinVertical } from /* faCheckCircle */ '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import medalImg from 'images/icons/gold-medal.svg';

const rootCss = ({ breakpoints, palette, shape, spacing, typography }) => {
  const mobileCss = {
    flexDirection: 'column',
    gap: spacing(1),
    alignItems: 'flex-start',
    padding: spacing(1, 2, 2),
    '.SCB-left': { gap: spacing(2) },
    '.SCB-img': { width: 40, height: 40 },
    '.SCB-title': { marginBottom: 0 },
    '.SCB-checks': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& > div': {
        '& > svg': { fontSize: 12 },
      },
    },
    '.SCB-btns': {
      width: '100%',
      flexDirection: 'column',
      gap: spacing(1),
      '& > *': {
        width: '100%',
        marginLeft: 0,
        fontSize: 13,
        padding: '4px 10px',
      },
    },
  };
  return {
    backgroundColor: palette.primary.main,
    color: palette.primary.contrastText,
    borderRadius: shape.borderRadiusSm,
    padding: spacing(3),
    display: 'flex',
    gap: spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    '&.root_free': {
      backgroundColor: palette.green.main,
      color: palette.green.contrastText,
    },
    '&.root_incourse': {
      margin: spacing(1, 1, 0),
    },
    '.SCB-left': { display: 'flex', gap: spacing(3), alignItems: 'center' },
    '.SCB-img': { width: 60, height: 60 },
    '.SCB-title': { marginBottom: spacing(0.5) },
    '.SCB-lightText': { fontWeight: typography.fontWeightRegular },
    '.SCB-checks': {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        '&:not(:last-of-type)': { marginRight: spacing(3) },
        '& svg': { marginRight: spacing(1) },
      },
    },
    '.SCB-btns': {
      flex: '0 0 auto',
      display: 'flex',
      gap: spacing(2),
    },
    [breakpoints.down(800)]: mobileCss,
    '@container (max-width: 800px)': mobileCss,
  };
};

// use `hidden` prop so that checkout dialog remains open and shows "success" state & fires event
// after successful checkout (without unmounting checkout dialog)
export default function SubscribeCTABanner({
  className,
  course,
  currentUser,
  freeActivity,
  hidden,
  inCourse,
  isMcat,
  onlineCourse,
  onlineCourseUser,
  onSignUp,
  subscription,
  tokenPurchased,
}) {
  const [checkoutDialogOpen, checkoutDialogProps, checkoutDialogActions] = useDialogStateManager();

  return (
    <>
      {!hidden && renderBanner()}
      {checkoutDialogProps && (
        <SubscriptionCheckout
          defaultProduct={isMcat && 'premed_plus'}
          onClose={checkoutDialogActions.close}
          open={checkoutDialogOpen}
        />
      )}
    </>
  );

  function renderBanner() {
    const [leftContent, ctas] = getContent();
    return (
      <div
        className={classNames(className, { root_free: freeActivity || tokenPurchased, root_incourse: inCourse })}
        css={rootCss}
      >
        <div className="SCB-left">
          {freeActivity || tokenPurchased ?
            <Avatar className="SCB-img" sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: 'inherit' }}>
              <FontAwesomeIcon icon={faCoinVertical} size="xl" />
            </Avatar>
          : <img alt="medal" className="SCB-img" src={medalImg} />}
          <div>
            <Typography
              className="SCB-title"
              sx={{
                color: 'inherit',
              }}
              // sx={{ fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' } }}
              variant="h6"
            >
              {freeActivity ?
                <>
                  Free Activity{' '}
                  {!!FREE_TOKEN_COUNT && (
                    <span className="SCB-lightText">Does not count toward free trial activities</span>
                  )}
                </>
              : tokenPurchased ?
                'Free Activity Redeemed!'
              : <>
                  <span className="SCB-lightText">
                    {!inCourse && course ? `${course.code} On-Demand Prep Included ` : 'Included '}in
                  </span>{' '}
                  {isMcat ? 'Pre-Med Plus' : WIZE_SUBSCRIPTION_NAME}
                </>
              }
            </Typography>
            {leftContent}
          </div>
        </div>
        <div className="SCB-btns">
          {ctas?.filter(Boolean)?.map((cta, idx, arr) => (
            <CustomColorButton
              key={idx}
              color={idx === arr.length - 1 ? `white_${freeActivity || tokenPurchased ? 'green' : 'primary'}` : 'white'}
              size="large"
              variant={idx === arr.length - 1 ? 'contained' : 'outlined'}
              {...cta}
            />
          ))}
        </div>
      </div>
    );
  }

  function getContent() {
    /* eslint-disable @eslint-react/no-missing-key */
    if (hidden) return [];
    if (currentUser.guest) {
      return [
        !!FREE_TOKEN_COUNT && (
          <Typography variant="subtitle2">
            {`Unlock ${FREE_TOKEN_COUNT} free activities `}
            <Typography component="span" variant="body2">
              when you sign up for an account.
            </Typography>
          </Typography>
        ),
        // <div className="SCB-checks">
        //   {['Course Library Access', 'Expert Answers', 'Cancel Anytime'].map(t => (
        //     <div key={t}>
        //       <FontAwesomeIcon icon={faCheckCircle} />
        //       <Typography component="p" variant="subtitle2">
        //         {t}
        //       </Typography>
        //     </div>
        //   ))}
        // </div>,
        [{ children: FREE_TOKEN_COUNT ? 'Start learning for free' : 'Create a free account', onClick: onSignUp }],
      ];
    }
    if (freeActivity) return [];
    const continueBtn = !inCourse &&
      onlineCourse && {
        children: `${hasProgress(onlineCourseUser) ? 'Continue' : 'Start'} Learning`,
        component: Link,
        nativeButton: false,
        to: onlineCourseResumePath(onlineCourse),
      };
    if (subscription) {
      const subStatus = getSubscriptionStatus(subscription);
      if (subStatus === 'active' || subStatus === 'trialing') {
        return [
          onlineCourse && (
            <OnlineCourseProgressBreakdown
              bgDark
              mini
              onlineCourse={onlineCourse}
              onlineCourseUser={onlineCourseUser}
            />
          ),
          [continueBtn],
        ];
      }
      if (subStatus === 'paused') {
        return [
          <Typography variant="subtitle2">Subscription paused</Typography>,
          [continueBtn && { ...continueBtn, disabled: true }],
        ];
      }
      if (getHasPaymentIssue(subscription)) {
        return [
          <Typography variant="subtitle2">
            Your last invoice payment failed. Please update your card informaition.
          </Typography>,
          [continueBtn, { children: 'Update Payment Info', onClick: checkoutDialogActions.open }],
        ];
      }
    }
    if (currentUser.token_balance) {
      return [
        <Typography variant="subtitle2">
          {pluralize(currentUser.token_balance, 'free activity')} remaining{' '}
          <Typography component="span" variant="body2">
            Subscribe for unlimited access to all activities, courses, and more
          </Typography>
        </Typography>,
        [continueBtn, { children: 'Subscribe', onClick: checkoutDialogActions.open }],
      ];
    }
    return [
      <Typography variant="subtitle2">
        {numberToCurrency(isMcat ? PREMED_PLUS_PRICE : SUBSCRIPTION_PRICE)} / Month.{' '}
        <Typography component="span" variant="body2">
          Cancel any time.
        </Typography>
      </Typography>,
      [{ children: 'Subscribe', onClick: checkoutDialogActions.open }],
    ];
    /* eslint-enable @eslint-react/no-missing-key */
  }
}
